import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SendIcon from '@mui/icons-material/Send';
import PaidIcon from '@mui/icons-material/Paid';
import BlockIcon from '@mui/icons-material/Block';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ReplayIcon from '@mui/icons-material/Replay';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import InventoryIcon from '@mui/icons-material/Inventory2Outlined';
import { format } from 'date-fns';
import { CardSkeleton, EmptyState, PageHeader, StatusChip } from '../../components/ui';
import {
  apiErrorMessage,
  invoicesService,
  serviceCatalogService,
  inventoryService,
  paymentsService,
  squarePaymentsService,
  stripePaymentsService,
  taxTypesService,
} from '../../services/api';
import { useSocket } from '../../contexts/SocketContext';
import { useShop } from '../../contexts/ShopContext';
import { useCachedData } from '../../hooks/useCachedData';
import {
  Invoice,
  InvoiceStatus,
  Part,
  Payment,
  PaymentMethod,
  ServiceCatalogItem,
  PaymentProvider,
  PaymentStatus,
  SquareConnectionStatus,
  StripeConnectionStatus,
  TaxType,
} from '../../types';

/** Coerces before formatting: DECIMAL columns can arrive as strings. */
const formatAmount = (value: number | string | null | undefined) => {
  const amount = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(amount) ? `$${amount.toFixed(2)}` : '$0.00';
};

const toNumber = (value: number | string | null | undefined) => {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/** Line rows are held as strings so a half-typed "12." doesn't fight the input. */
interface DraftLine {
  description: string;
  quantity: string;
  unitPrice: string;
  taxTypeId: string;
  /** Set when the line sells a stocked part; issuing the invoice consumes it. */
  partId?: string | null;
}

const emptyLine: DraftLine = { description: '', quantity: '1', unitPrice: '0', taxTypeId: '' };

/** Invoices per page. Two-column card grid, so a multiple of two reads evenly. */
const PAGE_SIZE = 20;

const TERMINAL_PROVIDERS = [PaymentProvider.SQUARE_TERMINAL, PaymentProvider.STRIPE_TERMINAL];

/** How a ledger row names itself: external rows by method, card rows by channel. */
const paymentLabel = (payment: Payment) => {
  if (payment.provider === PaymentProvider.SQUARE_ONLINE) return 'Card (online)';
  if (payment.provider === PaymentProvider.SQUARE_TERMINAL) return 'Card (terminal)';
  if (payment.provider === PaymentProvider.STRIPE_ONLINE) return 'Card (online, Stripe)';
  if (payment.provider === PaymentProvider.STRIPE_TERMINAL) return 'Card (reader, Stripe)';
  const method = payment.method || 'other';
  return method.charAt(0).toUpperCase() + method.slice(1);
};

const NO_INVOICES: Invoice[] = [];
const NO_TAX_TYPES: TaxType[] = [];

const InvoicePage: React.FC = () => {
  const { socket } = useSocket();
  const { selectedShop } = useShop();
  // Server-side paging: the list used to fetch every invoice a shop had ever
  // issued, each with its items, taxes and payments eager-loaded.
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState<InvoiceStatus | 'all'>('all');
  // From the session cache first, then reloaded: returning here paints at once.
  const invoicesData = useCachedData(`invoices:${page}:${statusFilter}`, () =>
    invoicesService.getInvoicesPage({
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      ...(statusFilter === 'all' ? {} : { status: statusFilter }),
    }),
  );
  const taxTypesData = useCachedData('tax-types', () => taxTypesService.getTaxTypes());
  const invoices = invoicesData.data?.items ?? NO_INVOICES;
  const taxTypes = taxTypesData.data ?? NO_TAX_TYPES;
  const loading = invoicesData.loading;
  const loadError = invoicesData.error || taxTypesData.error;
  const { mutate: mutateInvoicePage, reload: loadInvoices } = invoicesData;
  const setInvoices = useCallback(
    (update: (previous: Invoice[]) => Invoice[]) =>
      mutateInvoicePage((previous) => ({ ...previous, items: update(previous.items) })),
    [mutateInvoicePage],
  );
  // The pager keeps the last known total while another page loads.
  const lastTotal = useRef(0);
  if (invoicesData.data) lastTotal.current = invoicesData.data.total;
  const total = invoicesData.data?.total ?? lastTotal.current;
  const [squareStatus, setSquareStatus] = useState<SquareConnectionStatus | null>(null);
  const [stripeStatus, setStripeStatus] = useState<StripeConnectionStatus | null>(null);
  const [parts, setParts] = useState<Part[]>([]);
  const [partsAnchor, setPartsAnchor] = useState<HTMLElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [editing, setEditing] = useState<Invoice | null>(null);
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [catalog, setCatalog] = useState<ServiceCatalogItem[]>([]);
  const [catalogAnchor, setCatalogAnchor] = useState<HTMLElement | null>(null);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // Record-payment form (held as strings, same as the line editor).
  const [showPayForm, setShowPayForm] = useState(false);
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState<PaymentMethod>(PaymentMethod.CASH);
  const [payNote, setPayNote] = useState('');

  // Card processor connections — decide whether "Charge on terminal" is offered.
  useEffect(() => {
    let active = true;
    squarePaymentsService
      .getStatus(selectedShop?.id)
      .then((status) => active && setSquareStatus(status))
      .catch(() => active && setSquareStatus(null)); // not configured — hide the button
    stripePaymentsService
      .getStatus(selectedShop?.id)
      .then((status) => active && setStripeStatus(status))
      .catch(() => active && setStripeStatus(null));
    return () => {
      active = false;
    };
  }, [selectedShop]);

  // Stocked parts for the "From stock" picker. Like the catalogue, a failure
  // here must not stop anyone pricing an invoice by hand.
  useEffect(() => {
    let cancelled = false;
    inventoryService
      .listParts({ activeOnly: true, limit: 200, shopId: selectedShop?.id })
      .then(({ rows }) => !cancelled && setParts(rows))
      .catch(() => !cancelled && setParts([]));
    return () => {
      cancelled = true;
    };
  }, [selectedShop]);

  // Live updates: a terminal checkout resolves via webhook, so the open
  // dialog refreshes itself when the gateway pushes the invoice.
  useEffect(() => {
    if (!socket) return;

    const onInvoiceUpdated = (invoice: Invoice) => {
      setInvoices((prev) => prev.map((row) => (row.id === invoice.id ? invoice : row)));
      setEditing((prev) => (prev && prev.id === invoice.id ? invoice : prev));
    };
    const onPaymentUpdated = (payment: Payment) => {
      setEditing((prev) => {
        if (!prev || prev.id !== payment.invoiceId) return prev;
        const rest = (prev.payments ?? []).filter((row) => row.id !== payment.id);
        return { ...prev, payments: [...rest, payment] };
      });
    };

    socket.on('invoice_updated', onInvoiceUpdated);
    socket.on('payment_updated', onPaymentUpdated);
    return () => {
      socket.off('invoice_updated', onInvoiceUpdated);
      socket.off('payment_updated', onPaymentUpdated);
    };
  }, [socket, setInvoices]);

  const openEditor = (invoice: Invoice) => {
    setEditing(invoice);
    setNotes(invoice.notes || '');
    setLines(
      (invoice.items || []).map((item) => ({
        description: item.description,
        quantity: String(toNumber(item.quantity)),
        unitPrice: String(toNumber(item.unitPrice)),
        taxTypeId: item.taxTypeId || '',
        partId: item.partId ?? null,
      })),
    );
  };

  const closeEditor = () => {
    setEditing(null);
    setLines([]);
    setNotes('');
    setShowPayForm(false);
    setPayAmount('');
    setPayMethod(PaymentMethod.CASH);
    setPayNote('');
  };

  const isPaid = editing?.status === InvoiceStatus.PAID;
  const amountPaid = editing ? toNumber(editing.amountPaid) : 0;
  const balanceDue = editing
    ? toNumber(editing.balanceDue ?? toNumber(editing.amountDue) - amountPaid)
    : 0;
  // The backend also rejects edits once money is recorded — mirror that here.
  const locked = isPaid || amountPaid > 0;
  const ledger = editing?.payments ?? [];

  const updateLine = (index: number, patch: Partial<DraftLine>) =>
    setLines((prev) => prev.map((line, i) => (i === index ? { ...line, ...patch } : line)));

  const defaultTaxTypeId = useMemo(
    () => taxTypes.find((type) => type.isDefault)?.id || '',
    [taxTypes],
  );

  /**
   * Preview only — the server recomputes from the same lines and wins. Mirrors
   * the backend: components are bucketed by (name, rate) across the whole
   * invoice and rounded once, so the rows always add up to the total.
   */
  const preview = useMemo(() => {
    const round = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;
    const buckets = new Map<string, { name: string; rate: number; base: number }>();
    let subtotal = 0;

    for (const line of lines) {
      const lineTotal = round(toNumber(line.quantity) * toNumber(line.unitPrice));
      subtotal = round(subtotal + lineTotal);

      const taxType = taxTypes.find((type) => type.id === line.taxTypeId);
      for (const component of taxType?.components ?? []) {
        const rate = toNumber(component.rate);
        if (rate <= 0) continue;
        const key = `${component.name}::${rate}`;
        const bucket = buckets.get(key) ?? { name: component.name, rate, base: 0 };
        bucket.base = round(bucket.base + lineTotal);
        buckets.set(key, bucket);
      }
    }

    const rows = Array.from(buckets.values())
      .map((bucket) => ({ name: bucket.name, rate: bucket.rate, amount: round(bucket.base * bucket.rate) }))
      .filter((row) => row.amount !== 0)
      .sort((a, b) => b.rate - a.rate || a.name.localeCompare(b.name));

    const taxTotal = round(rows.reduce((sum, row) => sum + row.amount, 0));
    return { subtotal, rows, taxTotal, total: round(subtotal + taxTotal) };
  }, [lines, taxTypes]);

  const previewSubtotal = preview.subtotal;

  const runAction = async (action: () => Promise<unknown>, message: string) => {
    try {
      setSaving(true);
      setError(null);
      await action();
      setSuccessMessage(message);
      await loadInvoices();
      return true;
    } catch (err: any) {
      setError(apiErrorMessage(err, 'Something went wrong'));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveLines = async () => {
    if (!editing) return;
    if (lines.some((line) => !line.description.trim())) {
      setError('Every line needs a description');
      return;
    }

    const ok = await runAction(async () => {
      await invoicesService.replaceInvoiceItems(
        editing.id,
        lines.map((line) => ({
          description: line.description.trim(),
          quantity: toNumber(line.quantity),
          unitPrice: toNumber(line.unitPrice),
          taxTypeId: line.taxTypeId || null,
          partId: line.partId || null,
        })),
      );
      if ((editing.notes || '') !== notes) {
        await invoicesService.updateInvoice(editing.id, { notes });
      }
    }, 'Invoice saved');

    if (ok) closeEditor();
  };

  /**
   * Saves the PDF the API renders. The blob is turned into an object URL and
   * revoked straight after — leaving it attached keeps the whole file in memory
   * for the life of the tab.
   */
  const handleDownloadPdf = async () => {
    if (!editing) return;

    setSaving(true);
    try {
      const { blob, filename } = await invoicesService.downloadInvoicePdf(editing.id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not produce the PDF.'));
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async () => {
    if (!editing) return;
    const ok = await runAction(
      () => invoicesService.sendInvoice(editing.id),
      isPaid
        ? 'Receipt emailed to the customer'
        : 'Invoice emailed to the customer',
    );
    if (ok) closeEditor();
  };

  /**
   * Records an external payment (cash/check/card-on-other-terminal). Leaving
   * the amount blank settles the full balance — the old "mark paid" gesture.
   * The dialog stays open so the updated ledger is visible.
   */
  const handleRecordPayment = async () => {
    if (!editing) return;
    const amount = payAmount.trim() === '' ? undefined : toNumber(payAmount);
    if (amount !== undefined && amount <= 0) {
      setError('Payment amount must be greater than zero');
      return;
    }

    const ok = await runAction(async () => {
      const updated = await paymentsService.recordExternal({
        invoiceId: editing.id,
        amount,
        method: payMethod,
        note: payNote.trim() || undefined,
      });
      setEditing(updated);
    }, 'Payment recorded');

    if (ok) {
      setShowPayForm(false);
      setPayAmount('');
      setPayNote('');
    }
  };

  const handleVoidPayment = async (payment: Payment) => {
    if (!editing) return;
    if (!window.confirm('Void this payment? The invoice balance will reopen.')) return;
    await runAction(async () => {
      const updated = await paymentsService.voidPayment(payment.id);
      setEditing(updated);
    }, 'Payment voided');
  };

  /**
   * Refunds a card payment. The prompt takes an amount so a partial refund is
   * possible; leaving it as the suggested value refunds what is left.
   */
  const handleRefundPayment = async (payment: Payment) => {
    if (!editing) return;

    // DECIMALs can arrive as strings; coerce before any arithmetic.
    const refundable = Number(payment.amount) - Number(payment.refundedAmount ?? 0);
    const entered = window.prompt(
      `Refund how much of ${formatAmount(payment.amount)}? Up to ${formatAmount(refundable)} is left.`,
      refundable.toFixed(2),
    );
    if (entered === null) return;

    const amount = Number(entered);
    if (!Number.isFinite(amount) || amount <= 0 || amount > refundable + 0.005) {
      setError(`Enter an amount between 0.01 and ${formatAmount(refundable)}.`);
      return;
    }

    await runAction(async () => {
      const updated = await paymentsService.refundPayment(payment.id, { amount });
      setEditing(updated);
    }, 'Refund issued');
  };

  // The catalogue is a convenience for pricing, so a failure to load it must
  // not stop anyone editing an invoice by hand.
  useEffect(() => {
    let cancelled = false;
    serviceCatalogService
      .list({ activeOnly: true })
      .then((items) => {
        if (!cancelled) setCatalog(items);
      })
      .catch(() => {
        if (!cancelled) setCatalog([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /** Copies a catalogue item onto a new line. The line is independent after this. */
  const addLineFromCatalog = (item: ServiceCatalogItem) => {
    setLines((prev) => [
      ...prev,
      {
        description: item.name,
        quantity: String(Number(item.defaultQuantity) || 1),
        unitPrice: Number(item.defaultPrice).toFixed(2),
        taxTypeId: item.taxTypeId || defaultTaxTypeId,
      },
    ]);
    setCatalogAnchor(null);
  };

  /** A stocked part as a line. Issuing the invoice takes it out of stock. */
  const addLineFromPart = (part: Part) => {
    setLines((prev) => [
      ...prev,
      {
        description: [part.brand, part.partNumber, '—', part.name].filter(Boolean).join(' '),
        quantity: '1',
        unitPrice: toNumber(part.unitPrice).toFixed(2),
        taxTypeId: part.taxTypeId || defaultTaxTypeId,
        partId: part.id,
      },
    ]);
    setPartsAnchor(null);
  };

  const pendingTerminal = ledger.find(
    (payment) =>
      TERMINAL_PROVIDERS.includes(payment.provider) &&
      payment.status === PaymentStatus.PENDING,
  );
  // Mirrors the server's choice: Stripe when connected as primary, else Square.
  const stripeIsPrimary = Boolean(stripeStatus?.connected && stripeStatus.primary);
  const canChargeTerminal = stripeIsPrimary
    ? Boolean(stripeStatus?.readerId)
    : Boolean(
        squareStatus?.connected && squareStatus.allowed !== false && squareStatus.deviceId,
      );

  /** Pushes the balance to the shop's terminal; the webhook settles it. */
  const handleChargeTerminal = async () => {
    if (!editing) return;
    await runAction(async () => {
      await paymentsService.createTerminalCheckout({ invoiceId: editing.id });
      setEditing(await invoicesService.getInvoice(editing.id));
    }, 'Checkout sent to the terminal — waiting for the customer');
  };

  const handleCancelTerminal = async (payment: Payment) => {
    await runAction(async () => {
      const updated = await paymentsService.cancelTerminalCheckout(payment.id);
      setEditing(updated);
    }, 'Terminal checkout canceled');
  };

  const handleDelete = async () => {
    if (!editing) return;
    if (!window.confirm('Delete this draft invoice? Its line items go with it.')) return;
    const ok = await runAction(() => invoicesService.deleteInvoice(editing.id), 'Draft invoice deleted');
    if (ok) closeEditor();
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <PageHeader
        title="Invoices"
        subtitle="Generated from completed jobs. Price the lines, send to the customer, then record their payment."
        actions={
          <TextField
            select
            size="small"
            label="Status"
            value={statusFilter}
            onChange={(event) => {
              // A filter change re-queries from the first page; keeping the
              // offset would land on an empty page of a smaller result set.
              setStatusFilter(event.target.value as InvoiceStatus | 'all');
              setPage(0);
            }}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value={InvoiceStatus.DRAFT}>Draft</MenuItem>
            <MenuItem value={InvoiceStatus.SENT}>Sent</MenuItem>
            <MenuItem value={InvoiceStatus.PAID}>Paid</MenuItem>
          </TextField>
        }
      />

      {loadError ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {apiErrorMessage(loadError, 'Failed to load invoices')}
        </Alert>
      ) : null}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {loading ? (
        <CardSkeleton count={4} />
      ) : invoices.length === 0 ? (
        <EmptyState
          icon={<ReceiptLongIcon />}
          title="No invoices yet"
          description="Invoices appear here once you generate one from a completed job on the Appointments page."
        />
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
            gap: 2,
          }}
        >
          {invoices.map((invoice) => (
            <Paper key={invoice.id} variant="outlined" sx={{ p: 2.5 }}>
              <Stack spacing={1.25}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {invoice.appointment?.customerName || 'Appointment'}
                  </Typography>
                  <StatusChip status={invoice.status} size="small" />
                </Box>

                <Typography variant="body2" color="text.secondary">
                  {invoice.appointment?.vehicleModel}
                </Typography>

                <Typography variant="h6">{formatAmount(invoice.amountDue)}</Typography>

                <Typography variant="caption" color="text.secondary">
                  {invoice.items?.length || 0} line
                  {(invoice.items?.length || 0) === 1 ? '' : 's'} • generated{' '}
                  {format(new Date(Number(invoice.generatedAt) * 1000), 'PP')}
                </Typography>

                <Box>
                  <Button size="small" onClick={() => openEditor(invoice)}>
                    {invoice.status === InvoiceStatus.PAID ? 'View' : 'Open'}
                  </Button>
                </Box>
              </Stack>
            </Paper>
          ))}
        </Box>
      )}

      {total > PAGE_SIZE && (
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="center"
          sx={{ mt: 3 }}
        >
          <Button
            size="small"
            disabled={page === 0 || loading}
            onClick={() => setPage((current) => Math.max(0, current - 1))}
          >
            Previous
          </Button>
          <Typography variant="body2" color="text.secondary">
            {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
          </Typography>
          <Button
            size="small"
            disabled={(page + 1) * PAGE_SIZE >= total || loading}
            onClick={() => setPage((current) => current + 1)}
          >
            Next
          </Button>
        </Stack>
      )}

      <Dialog open={Boolean(editing)} onClose={closeEditor} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          Invoice — {editing?.appointment?.customerName}
          {editing && <StatusChip status={editing.status} size="small" />}
        </DialogTitle>

        <DialogContent dividers>
          {locked && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {isPaid
                ? 'This invoice is paid and can no longer be edited.'
                : 'This invoice has a payment recorded against it. Void the payment to edit the lines.'}
            </Alert>
          )}

          <Stack spacing={1}>
            {lines.map((line, index) => (
              <Box
                key={index}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 80px 110px 150px 40px' },
                  gap: 1,
                  alignItems: 'center',
                }}
              >
                <TextField
                  size="small"
                  label={index === 0 ? 'Description' : undefined}
                  value={line.description}
                  disabled={locked}
                  onChange={(e) => updateLine(index, { description: e.target.value })}
                  InputProps={
                    line.partId
                      ? {
                          startAdornment: (
                            <Tooltip title="From stock — sending the invoice takes it off the shelf">
                              <InventoryIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                            </Tooltip>
                          ),
                        }
                      : undefined
                  }
                />
                <TextField
                  size="small"
                  label={index === 0 ? 'Qty' : undefined}
                  type="number"
                  value={line.quantity}
                  disabled={locked}
                  onChange={(e) => updateLine(index, { quantity: e.target.value })}
                  inputProps={{ min: 0, step: 0.5 }}
                />
                <TextField
                  size="small"
                  label={index === 0 ? 'Unit price' : undefined}
                  type="number"
                  value={line.unitPrice}
                  disabled={locked}
                  onChange={(e) => updateLine(index, { unitPrice: e.target.value })}
                  inputProps={{ min: 0, step: 0.01 }}
                />
                <TextField
                  size="small"
                  select
                  label={index === 0 ? 'Tax' : undefined}
                  value={line.taxTypeId}
                  disabled={locked}
                  onChange={(e) => updateLine(index, { taxTypeId: e.target.value })}
                >
                  <MenuItem value="">
                    <em>No tax</em>
                  </MenuItem>
                  {taxTypes
                    .filter((type) => type.isActive || type.id === line.taxTypeId)
                    .map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.name}
                        {type.isDefault ? ' (default)' : ''}
                      </MenuItem>
                    ))}
                </TextField>
                <IconButton
                  aria-label="Remove line"
                  disabled={locked}
                  onClick={() => setLines((prev) => prev.filter((_, i) => i !== index))}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}

            {!locked && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() =>
                    setLines((prev) => [...prev, { ...emptyLine, taxTypeId: defaultTaxTypeId }])
                  }
                >
                  Add line
                </Button>

                {parts.length > 0 && (
                  <>
                    <Button
                      size="small"
                      startIcon={<InventoryIcon />}
                      onClick={(event) => setPartsAnchor(event.currentTarget)}
                    >
                      From stock
                    </Button>
                    <Menu
                      anchorEl={partsAnchor}
                      open={Boolean(partsAnchor)}
                      onClose={() => setPartsAnchor(null)}
                      slotProps={{ paper: { sx: { maxHeight: 360 } } }}
                    >
                      {parts.map((part) => (
                        <MenuItem key={part.id} onClick={() => addLineFromPart(part)}>
                          <ListItemText
                            primary={`${part.name} · ${part.sku}`}
                            secondary={`${formatAmount(part.unitPrice)} — ${toNumber(part.onHand)} on hand`}
                          />
                        </MenuItem>
                      ))}
                    </Menu>
                  </>
                )}

                {catalog.length > 0 && (
                  <>
                    <Button
                      size="small"
                      startIcon={<MenuBookIcon />}
                      onClick={(event) => setCatalogAnchor(event.currentTarget)}
                    >
                      From catalog
                    </Button>
                    <Menu
                      anchorEl={catalogAnchor}
                      open={Boolean(catalogAnchor)}
                      onClose={() => setCatalogAnchor(null)}
                    >
                      {catalog.map((item) => (
                        <MenuItem key={item.id} onClick={() => addLineFromCatalog(item)}>
                          <ListItemText
                            primary={item.name}
                            secondary={formatAmount(item.defaultPrice)}
                          />
                        </MenuItem>
                      ))}
                    </Menu>
                  </>
                )}
              </Box>
            )}

            <Divider sx={{ my: 1 }} />

            <Stack spacing={0.5} sx={{ alignSelf: 'flex-end', minWidth: 240 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Subtotal
                </Typography>
                <Typography variant="body2">{formatAmount(previewSubtotal)}</Typography>
              </Box>
              {preview.rows.map((row) => (
                <Box key={`${row.name}-${row.rate}`} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    {row.name} ({(row.rate * 100).toFixed(2)}%)
                  </Typography>
                  <Typography variant="body2">{formatAmount(row.amount)}</Typography>
                </Box>
              ))}
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2">Total</Typography>
                <Typography variant="subtitle2">{formatAmount(preview.total)}</Typography>
              </Box>
            </Stack>

            <TextField
              label="Notes"
              multiline
              minRows={2}
              value={notes}
              disabled={locked}
              onChange={(e) => setNotes(e.target.value)}
              sx={{ mt: 1 }}
            />

            {/* Payment ledger — drafts can't take payments, so hide it there. */}
            {editing && editing.status !== InvoiceStatus.DRAFT && (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2">Payments</Typography>

                {ledger.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No payments recorded yet.
                  </Typography>
                )}

                {ledger.map((payment) => (
                  <Box
                    key={payment.id}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                  >
                    <Typography variant="body2" sx={{ flexGrow: 1, minWidth: 0 }} noWrap>
                      {paymentLabel(payment)}
                      {payment.note ? ` — ${payment.note}` : ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {format(
                        new Date((payment.receivedAt ?? Number(payment.createdAt)) * 1000),
                        'PP',
                      )}
                    </Typography>
                    <StatusChip status={payment.status} size="small" />
                    <Typography
                      variant="body2"
                      sx={{
                        minWidth: 72,
                        textAlign: 'right',
                        ...(payment.status !== PaymentStatus.COMPLETED && {
                          textDecoration: 'line-through',
                          color: 'text.disabled',
                        }),
                      }}
                    >
                      {formatAmount(payment.amount)}
                    </Typography>
                    {Number(payment.refundedAmount ?? 0) > 0 && (
                      <Typography variant="caption" color="warning.main" sx={{ whiteSpace: 'nowrap' }}>
                        −{formatAmount(payment.refundedAmount ?? 0)} refunded
                      </Typography>
                    )}
                    {payment.provider === PaymentProvider.EXTERNAL &&
                      payment.status === PaymentStatus.COMPLETED && (
                        <Tooltip title="Void payment">
                          <span>
                            <IconButton
                              size="small"
                              aria-label="Void payment"
                              disabled={saving}
                              onClick={() => handleVoidPayment(payment)}
                            >
                              <BlockIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    {/* Card payments go back through the processor that took
                        them, not the void path — the void path only corrects a
                        mis-keyed cash or cheque entry. */}
                    {payment.provider !== PaymentProvider.EXTERNAL &&
                      payment.status === PaymentStatus.COMPLETED &&
                      Number(payment.amount) - Number(payment.refundedAmount ?? 0) > 0 && (
                        <Tooltip title="Refund to the card">
                          <span>
                            <IconButton
                              size="small"
                              aria-label="Refund payment"
                              disabled={saving}
                              onClick={() => handleRefundPayment(payment)}
                            >
                              <ReplayIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    {TERMINAL_PROVIDERS.includes(payment.provider) &&
                      payment.status === PaymentStatus.PENDING && (
                        <Button
                          size="small"
                          color="error"
                          disabled={saving}
                          onClick={() => handleCancelTerminal(payment)}
                        >
                          Cancel
                        </Button>
                      )}
                  </Box>
                ))}

                {pendingTerminal && (
                  <Alert severity="info">
                    Waiting for the customer to pay on the terminal…
                  </Alert>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="subtitle2">
                    {isPaid ? 'Paid in full' : 'Balance due'}
                  </Typography>
                  <Typography variant="subtitle2">
                    {formatAmount(isPaid ? amountPaid : balanceDue)}
                  </Typography>
                </Box>

                {showPayForm && !isPaid && (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '120px 130px 1fr auto' },
                      gap: 1,
                      alignItems: 'center',
                    }}
                  >
                    <TextField
                      size="small"
                      label="Amount"
                      type="number"
                      value={payAmount}
                      onChange={(e) => setPayAmount(e.target.value)}
                      inputProps={{ min: 0.01, step: 0.01 }}
                    />
                    <TextField
                      size="small"
                      select
                      label="Method"
                      value={payMethod}
                      onChange={(e) => setPayMethod(e.target.value as PaymentMethod)}
                    >
                      {Object.values(PaymentMethod).map((method) => (
                        <MenuItem key={method} value={method}>
                          {method.charAt(0).toUpperCase() + method.slice(1)}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      size="small"
                      label="Note (optional)"
                      value={payNote}
                      onChange={(e) => setPayNote(e.target.value)}
                    />
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleRecordPayment}
                      disabled={saving}
                    >
                      Save payment
                    </Button>
                  </Box>
                )}
              </>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
          <Box>
            {editing?.status === InvoiceStatus.DRAFT && (
              <Button color="error" onClick={handleDelete} disabled={saving}>
                Delete draft
              </Button>
            )}
          </Box>

          <Stack direction="row" spacing={1}>
            <Button onClick={closeEditor}>Close</Button>

            {!locked && (
              <Button onClick={handleSaveLines} disabled={saving}>
                Save
              </Button>
            )}

            <Button
              startIcon={<PictureAsPdfIcon />}
              onClick={handleDownloadPdf}
              disabled={saving}
            >
              Download PDF
            </Button>

            {/* Paid invoices can still be emailed — the customer gets a
                receipt rather than a second request for payment. */}
            <Tooltip
              title={
                previewSubtotal > 0 ? '' : 'Price the line items before sending'
              }
            >
              <span>
                <Button
                  startIcon={<SendIcon />}
                  onClick={handleSend}
                  disabled={saving || previewSubtotal <= 0}
                >
                  {isPaid
                    ? 'Send receipt'
                    : editing?.status === InvoiceStatus.SENT
                      ? 'Resend'
                      : 'Send'}
                </Button>
              </span>
            </Tooltip>

            {editing?.status === InvoiceStatus.SENT && !pendingTerminal && (
              <Tooltip
                title={
                  canChargeTerminal
                    ? ''
                    : stripeIsPrimary
                      ? 'Add a Stripe Terminal reader ID under Integrations'
                      : 'Connect Square and set a terminal device ID in shop settings'
                }
              >
                <span>
                  <Button
                    startIcon={<PointOfSaleIcon />}
                    onClick={handleChargeTerminal}
                    disabled={saving || !canChargeTerminal}
                  >
                    Charge on terminal
                  </Button>
                </span>
              </Tooltip>
            )}

            {editing?.status === InvoiceStatus.SENT && !showPayForm && (
              <Button
                variant="contained"
                startIcon={<PaidIcon />}
                onClick={() => {
                  setShowPayForm(true);
                  setPayAmount(balanceDue > 0 ? balanceDue.toFixed(2) : '');
                }}
                disabled={saving}
              >
                Record payment
              </Button>
            )}
          </Stack>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default InvoicePage;
